import React, { useEffect, useState } from 'react';
import BreadcrumbsBar from "../../layouts/breadcrumb/BreadcrumbsBar";
import Card from "../../common/card/Card";
import { useTranslation } from "react-i18next";
import GenericTable from "../../common/table/GenericTable";
import {
    addNewUser,
    getAllHasOfferUsers,
    updateUserGeneralSettings,
    generateBankSettingsUpdateLink,
    restoreUserAccount
} from "../../services/usersService";
import UserBankDetails from "./UserBankDetail/UserBankDetails";
import { Button, TextField, InputAdornment, IconButton, Tooltip } from "@mui/material";
import { LaunchOutlined, UpdateOutlined, LinkOutlined, ContentCopy } from "@mui/icons-material";
import { DOWNLOAD_HAS_OFFERS_USERS_CSV } from "../../common/constants/routes";
import styles from "./UsersAdmin.module.scss";
import Modal from "../../common/modal/Modal";
import { getInstagramLink } from "../../common/utils";
import Toast from "../../common/toast/Toast";
import useToast from "../../common/hooks/useToast";
import AddNewTuneUser from "./AddNewTuneUser/AddNewTuneUser";
import ExportDropdown from "../../common/exportDropdown/ExportDropdown";
import { EXPORT_FILE_TYPES } from "../AdvertiserManagement/CreateEditPaidProject/CreateEditPaidProject";

const UsersAdmin = () => {
    const { t } = useTranslation();
    const [tableData, setTableData] = useState([]);
    const { isToastOpen, toggleToast, toastContent, setToastContent } = useToast();
    const [linkModalState, setLinkModalState] = useState({ isOpen: false, link: null, error: false });
    const [restoreModalState, setRestoreModalState] = useState({ isOpen: false, user: null });
    const usersTableColumns = {};
    usersTableColumns.visibility = {}
    usersTableColumns.data = [
        /*  {
              accessorKey: 'user_id',
              header: "User Id",
              size:50
          },*/
        {
            accessorKey: 'isDeleted',
            header: "State",
            size: 250,
            Cell: (cell) => {
                const isDeleted = cell.row.original.isDeleted === 1 || cell.row.original.isDeleted === true || cell.row.original.isDeleted === "1";

                const handleRestoreClick = () => {
                   setRestoreModalState({ isOpen: true, user: cell.row.original });
                };

                return isDeleted ? (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={handleRestoreClick}
                    >
                        Deleted, want to revert back?
                    </Button>
                ) : null;
            }
        },
        {
            accessorKey: 'firstname',
            header: "Username",
            Cell: (cell) => (
                <div>{cell.row.original.firstname} {cell.row.original.lastname}</div>
            )
        },
        {
            accessorKey: 'hasoffersid',
            header: "HasOffer Id",
            size: 50
        },
        {
            accessorKey: 'email',
            header: t("common.email"),
            Cell: (cell) => (
                <TextField defaultValue={cell.row.original.email} onChange={(e => {
                    cell.row.original.email = e.target.value;
                })} name="email" size="small" variant="outlined" fullWidth />
            )
        },
        {
            accessorKey: 'phone',
            header: "Phone",
            size: 100,
            Cell: (cell) => (
                <TextField defaultValue={cell.row.original.phone} onChange={(e => {
                    cell.row.original.phone = e.target.value;
                })} name="phone" size="small" variant="outlined" fullWidth />
            )
        },
        {
            accessorKey: 'website',
            header: "Social Account",
            size: 200,
            Cell: (cell) => (
                <div>
                    <div className={styles.socialHolder}>
                        <TextField defaultValue={cell.row.original.website} onChange={(e => {
                            cell.row.original.website = e.target.value;
                        })} name="website" size="small" variant="outlined" fullWidth />
                        <Button
                            variant="contained"
                            sx={{ mT: 1, width: "fit-content" }}
                            startIcon={<LaunchOutlined />}
                            size={"small"}
                            onClick={(e) => {
                                let username = getInstagramLink(cell.row.original.website);
                                username = (username[0] === "/") ?
                                    username.substring(1) :
                                    username;
                                window.open(`https://www.instagram.com/${username}`, '_blank');
                            }}>{t("common.open")}
                        </Button>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'Actions',
            header: "Actions",
            Cell: (cell) => (
                <div>
                    <Button variant="contained"
                        color="info"
                        startIcon={<UpdateOutlined />}
                        size={"small"}
                        onClick={e => updateUserPersonalDetails(cell.row.original)}>{t("common.update")}
                    </Button>
                    <Button variant="contained"
                        color="secondary"
                        startIcon={<LinkOutlined />}
                        size={"small"}
                        sx={{ marginLeft: 1 }}
                        onClick={e => handleGenerateLink(cell.row.original)}>Generate Link
                    </Button>
                </div>
            )
        }
    ];

    const updateUserPersonalDetails = async (rowData) => {
        const {
            email,
            phone,
            website,
            user_id
        } = rowData;
        const response = await updateUserGeneralSettings({ email, phone, website, userId: user_id });
        (!!response.data) ?
            setToastContent(t("user-admin.update-success")) :
            setToastContent(t("user-admin.update-failed"));

        toggleToast();
    }

    const handleGenerateLink = async (rowData) => {
        const { email } = rowData;
        try {
            const response = await generateBankSettingsUpdateLink(email);
            if (!!response.data && response.data.link) {
                setLinkModalState({ isOpen: true, link: response.data.link, error: false });
            } else {
                setLinkModalState({ isOpen: true, link: null, error: true });
            }
        } catch (e) {
            setLinkModalState({ isOpen: true, link: null, error: true });
        }
    }

    const getAllUsers = async () => {
        const response = await getAllHasOfferUsers();
        if (!!response.data) {
            const users = response.data.users;
            const sortedUsers = users.sort((a, b) => a.user_id < b.user_id ? 1 : -1)
            setTableData(sortedUsers)
        }
    }

    const expandUser = ({ row }) => (
        <div>
            {!!row.original &&
                <UserBankDetails
                    bankDetail={row.original}></UserBankDetails>
            }
        </div>
    )


    useEffect(() => {
        getAllUsers();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps


    const handleDownloadFile = async (type) => {
        switch (type) {
            case EXPORT_FILE_TYPES.csv:
                window.open((process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_HAS_OFFERS_USERS_CSV + "?type=csv", "_blank");
                break;
            case EXPORT_FILE_TYPES.excel:
                window.open((process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_HAS_OFFERS_USERS_CSV + "?type=excel", "_blank");
                break;
            default:
                break;

        }
    }

    const callAddUserCallback = async (formData) => {
        const response = await addNewUser(formData);
        (!!response.data) ?
            setToastContent(t("user-admin.create-user-success-msg")) :
            setToastContent(t("user-admin.create-user-failed-msg"));
        toggleToast();
    }
    return (
        <div className={styles.container}>
            <BreadcrumbsBar />
            {tableData.length > 0 && <Card title={t("user-admin.title-users")}>
                <div>
                    <AddNewTuneUser addUserCb={callAddUserCallback} />
                </div>
                <div className={styles.buttonHolder}>
                    <ExportDropdown handleCb={handleDownloadFile} />
                </div>
                <GenericTable
                    outputColumns={usersTableColumns}
                    tableData={tableData}
                    allRowsAreExpanded={tableData.length === 1}
                    rowCount={tableData.length}
                    enablePagination={true}
                    expand={expandUser}
                />
            </Card>}
            <Modal
                isOpen={linkModalState.isOpen}
                onClose={() => setLinkModalState(prev => ({ ...prev, isOpen: false }))}
                title={linkModalState.error ? "Error" : "Secure Link Generated"}
                onCancelation={() => setLinkModalState(prev => ({ ...prev, isOpen: false }))}
                confirmationBtnText={null}
            >
                {linkModalState.error ? (
                    <div className={styles.linkModalContent}>
                        <div className={styles.infoText}>Failed to generate secure link. Please try again.</div>
                    </div>
                ) : (
                    <div className={styles.linkModalContent}>
                        <div className={styles.infoText}>
                            Share this secure link with the user. It allows them to safely update their bank details.
                        </div>
                        <div className={styles.linkBox}>
                            <TextField
                                value={linkModalState.link}
                                fullWidth
                                variant="outlined"
                                className={styles.linkField}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Copy to Clipboard">
                                                <IconButton
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(linkModalState.link);
                                                        setToastContent("Copied to clipboard!");
                                                        toggleToast();
                                                    }}
                                                    edge="end"
                                                >
                                                    <ContentCopy color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                            />
                        </div>
                    </div>
                )}
            </Modal>
            <Modal
                isOpen={restoreModalState.isOpen}
                onClose={() => setRestoreModalState({ isOpen: false, user: null })}
                title={"Restore User Account"}
                onCancelation={() => setRestoreModalState({ isOpen: false, user: null })}
                confirmationBtnText={"Yes, Restore"}
                onConfirmation={async () => {
                    if (restoreModalState.user) {
                        const response = await restoreUserAccount({ affId: restoreModalState.user.hasoffersid });
                        if (response.data) {
                            setToastContent("User account restored successfully.");
                            getAllUsers();
                        } else {
                            setToastContent("Failed to restore user account.");
                        }
                        toggleToast();
                    }
                    setRestoreModalState({ isOpen: false, user: null });
                }}
            >
                <div className={styles.linkModalContent}>
                    <div className={styles.infoText}>
                        Are you sure you want to restore the account for <strong>{restoreModalState.user?.firstname} {restoreModalState.user?.lastname}</strong>?
                    </div>
                </div>
            </Modal>
            <Toast
                isOpen={isToastOpen}
                onClose={toggleToast}
                content={toastContent}
                severity="info"
            />
        </div>
    );
};

export default UsersAdmin;
